import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

type AppRole = "officer" | "site_owner" | "business_owner" | "admin";

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: { full_name: string; phone: string | null; email: string | null; avatar_url: string | null; company_name: string | null; operator_license: string | null } | null;
  role: AppRole | null;
  roles: AppRole[];
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  session: null,
  profile: null,
  role: null,
  roles: [],
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const INTENDED_ROLE_KEY = "secureflex_intended_role";

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AuthState["profile"]>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProfileAndRole = async (userId: string) => {
    try {
      const fetchPromise = Promise.all([
        supabase.from("profiles").select("full_name, phone, email, avatar_url, company_name, operator_license").eq("id", userId).single(),
        supabase.from("user_roles").select("role").eq("user_id", userId),
      ]);

      // Add a 5-second timeout to prevent infinite loading if network hangs
      const timeoutPromise = new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000));
      
      const result = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (!result) {
        throw new Error("Network timeout while fetching user profile and roles.");
      }

      const [profileRes, roleRes] = result;
      
      if (profileRes.data) {
        setProfile(profileRes.data);
      }
      if (roleRes.data && roleRes.data.length > 0) {
        const allRoles = roleRes.data.map(r => r.role as AppRole);
        setRoles(allRoles);

        // Check if user has a stored intended role from login
        const intended = localStorage.getItem(INTENDED_ROLE_KEY) as AppRole | null;
        if (intended && allRoles.includes(intended)) {
          setRole(intended);
        } else {
          // Fallback to priority-based selection
          const priority: AppRole[] = ["admin", "business_owner", "site_owner", "officer"];
          const bestRole = priority.find(p => allRoles.includes(p)) || allRoles[0];
          setRole(bestRole);
        }
      } else {
        // CRITICAL FALLBACK: If user_roles table is empty (e.g. trigger delay), use metadata
        const userPromise = supabase.auth.getUser();
        const userTimeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000));
        const userResult = await Promise.race([userPromise, userTimeout]);
        
        if (userResult && userResult.data && userResult.data.user) {
          const metaRole = userResult.data.user.user_metadata?.role as AppRole;
          if (metaRole && ["officer", "site_owner", "business_owner", "admin"].includes(metaRole)) {
            console.log("[AUTH_FALLBACK] Using role from metadata:", metaRole);
            setRole(metaRole);
            setRoles([metaRole]);
            return;
          }
        }
        
        // Final fallback if metadata also fails, try fetching stored intent
        const storedRole = localStorage.getItem(INTENDED_ROLE_KEY) as AppRole | null;
        if (storedRole) {
          console.log("[AUTH_FALLBACK] Using stored intended role as emergency fallback:", storedRole);
          setRole(storedRole);
          setRoles([storedRole]);
        }
      }
    } catch (err) {
      console.error("[AUTH_ERR_PROFILE] Failed to fetch profile/role:", err);
      // Emergency fallback on error
      const storedRole = localStorage.getItem(INTENDED_ROLE_KEY) as AppRole | null;
      if (storedRole) {
        setRole(storedRole);
        setRoles([storedRole]);
      }
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfileAndRole(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          if (_event === "SIGNED_IN") {
            setLoading(true);
          }
          await fetchProfileAndRole(session.user.id);
          setLoading(false);
        } else {
          setProfile(null);
          setRole(null);
          setRoles([]);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    localStorage.removeItem(INTENDED_ROLE_KEY);
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRole(null);
    setRoles([]);
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, role, roles, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
